import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import type { RequestedFileFulfillmentStatus } from 'common/types/files';
import { styles } from './styles';
import { EmptyFileBoxProps } from './types';

const EmptyFileBox = ({
  requestedFile,
  handleUploadFiles,
  loadingFiles,
  progress,
  onMarkFulfillment,
  isMarkingFulfillment,
}: EmptyFileBoxProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [status, setStatus] = useState<RequestedFileFulfillmentStatus>('not_applicable');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (requestedFile.id) {
      setIsLoading(loadingFiles[requestedFile.id] || false);
    }
  }, [loadingFiles, requestedFile.id]);

  const isLoadingMemo = useMemo(() => isLoading, [isLoading]);
  const isFulfilledNa =
    requestedFile.fulfillment_status === 'not_applicable'
    || requestedFile.fulfillment_status === 'not_available';

  const handleConfirm = async () => {
    if (!onMarkFulfillment || !requestedFile.id) {
      return;
    }
    await onMarkFulfillment(requestedFile.id, status, note.trim() || null);
    setDialogOpen(false);
    setNote('');
  };

  if (isFulfilledNa) {
    const statusLabel =
      requestedFile.fulfillment_status === 'not_available' ? 'Not available' : 'Not applicable';
    return (
      <Box border={1} borderRadius={4} borderColor="#FFFFFF" p={2} mb={1} style={styles.container}>
        <Box display="flex" alignItems="center">
          <Box flexGrow={1} ml="1rem">
            <Typography style={styles.fileName}>{requestedFile.name}</Typography>
            <Typography variant="caption" color="textSecondary">
              {statusLabel}
              {requestedFile.fulfillment_note ? ` — ${requestedFile.fulfillment_note}` : ''}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box border={1} borderRadius={4} borderColor="#FFFFFF" p={2} mb={1} style={styles.container}>
      <Box display="flex" alignItems="center">
        <svg style={styles.svg} viewBox="0 0 32 38" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M17.0168 33.6667H4.00008C2.15008 33.6667 0.666748 32.1833 0.666748 30.3333V3.66666C0.666748 2.78261 1.01794 1.93476 1.64306 1.30964C2.26818 0.684518 3.11603 0.333328 4.00008 0.333328H17.3334L27.3334 10.3333V18.8167C26.7834 18.7333 26.2334 18.6667 25.6667 18.6667C25.1001 18.6667 24.5501 18.7333 24.0001 18.8167V12H15.6667V3.66666H4.00008V30.3333H15.8167C16.0167 31.5333 16.4334 32.65 17.0168 33.6667ZM32.3334 27H27.3334V22H24.0001V27H19.0001V30.3333H24.0001V35.3333H27.3334V30.3333H32.3334V27Z"
            fill="#043A6E"
          />
        </svg>
        <Box flexGrow={1} ml="3rem">
          <Typography style={styles.fileName}>{requestedFile.name}</Typography>
        </Box>
        {isLoadingMemo ? (
          <CircularProgress size={24} />
        ) : (
          <Box display="flex" gap={1}>
            <Button variant="outlined" component="label" style={styles.uploadButton}>
              Upload
              <input
                type="file"
                style={styles.hiddenInput}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleUploadFiles(event, requestedFile.id)}
                multiple
              />
            </Button>
            {onMarkFulfillment && (
              <Button
                variant="text"
                onClick={() => setDialogOpen(true)}
                disabled={isMarkingFulfillment}
                id={`mark-na-${requestedFile.id}`}
                data-testid="mark-requested-file-na-button"
              >
                N/A
              </Button>
            )}
          </Box>
        )}
      </Box>
      {isLoadingMemo && (
        <Box width="100%" mt={1}>
          <Typography variant="body2" color="textSecondary">{`Upload progress: ${progress}%`}</Typography>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Mark document as not applicable</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="dense"
            label="Status"
            value={status}
            onChange={event => setStatus(event.target.value as RequestedFileFulfillmentStatus)}
            id="mark-fulfillment-status"
          >
            <MenuItem value="not_applicable">Not applicable</MenuItem>
            <MenuItem value="not_available">Not available</MenuItem>
          </TextField>
          <TextField
            fullWidth
            margin="dense"
            label="Note"
            value={note}
            onChange={event => setNote(event.target.value)}
            multiline
            minRows={2}
            placeholder="Optional note (e.g. company has no convertible notes)"
            id="mark-fulfillment-note"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={isMarkingFulfillment}
            id="confirm-mark-fulfillment-button"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmptyFileBox;
